import { PopoverAlignment, PopoverShifting } from './constants';
import { getBaseGridPoint } from './ui-helpers';
import { UIElement } from '../components/index';
import { Country, LanguageLong, LanguageShort, VideoHost } from './enums';
import type {
    LanguageLabels,
    VideoHostLabel,
    PopoverConfig,
    PopoverHandler,
    PopoverHandlerResult,
} from './enums';
import type {
    NullOr,
    UndefinedOr,
    ObjectWithStringKeys,
    ObjectWithStringOrNumberKeys,
    Timeout,
    Debounce,
} from './utils.type';
import { ElementConfig } from '../components/ui-element.type';
import { featureFlags } from './feature-flags';
import { makeInvertiblePopoverHandler, makeMenuPopover } from './menu-popover';

/**
 * @memberof SharedComponents
 * @class Helpers
 * @alias helpers
 * @classdesc
 * Helpers for commonly used functions.
 */

/**
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing.
 * @memberof Helpers
 * @property {Function} [cancel] Belays debounce by clearing timeout.
 * @param {Function} func Function that needs to be executed.
 * @param {number} wait Time in milliseconds before function would be executed.
 * @param {boolean} [immediate] Executes function right away if set
 * on 'true', instead of after timeout when set to 'false'.
 * @returns {Function} Debounce result.
 */
const debounce = <TArgs extends Array<unknown>>(
    func: (...args: TArgs) => void,
    wait: number,
    immediate: boolean = false
): Debounce => {
    let timeout: NullOr<Timeout>;
    const result: Debounce = function (
        this: unknown,
        ...args: Array<unknown>
    ): void {
        const callNow = immediate && !timeout;
        clearTimeout(timeout || undefined);
        timeout = setTimeout(() => {
            timeout = null;
            if (!callNow) {
                func.apply(this, args as TArgs);
            }
        }, wait);
        if (callNow) {
            func.apply(this, args as TArgs);
        }
    };
    result.cancel = () => clearTimeout(timeout || undefined);
    return result;
};

/**
 * Returns a function, that, as long as it continues to be invoked, will be
 * triggered only once in a defined time. The function will be called
 * periodically every N milliseconds. If `immediate` is passed, trigger
 * the function on the leading edge, instead of the trailing.
 * @memberof Helpers
 * @param {Function} callback Function that needs to be executed.
 * @param {number} delay Time in milliseconds before function would be executed.
 * @param {boolean} [immediate] Executes function right away if set
 * on 'true', instead of after timeout when set to 'false'.
 * @returns {Function} Throttled result.
 */
const throttle = <TArgs extends Array<unknown>>(
    callback: (...args: TArgs) => void,
    delay: number,
    immediate: boolean = false
): ((...args: TArgs) => void) => {
    let throttled = false;
    return function (this: unknown, ...args: TArgs): void {
        if (throttled) {
            return;
        }
        if (immediate) {
            callback.apply(this, args);
        }
        throttled = true;
        setTimeout(() => {
            throttled = false;
            if (!immediate) {
                callback.apply(this, args);
            }
        }, delay);
    };
};

/**
 * Adds leading zero to numbers
 * @memberof Helpers
 * @param {string|number} value
 * @returns {string}
 */
const addLeadingZero = (value: string | number): string => {
    return String(value).replace(/^(-?)(\d)$/, '$10$2');
};

/**
 * Parses the string in format 'dd.mm.yyyy hh:mm:ss' or for Lithuania
 * 'yyyy-mm-dd hh:mm"ss' and returns the Date.
 * @memberof Helpers
 * @param {string} dateString
 * @returns {Date|null}
 */
const makeDateFromString = (dateString: string): NullOr<Date> => {
    let parts: RegExpMatchArray | NullOr<Array<string | number>> = null;

    const formats = [
        // ^ Start of the line
        // \d{2} two digits
        // . dot
        // \d{2} two digits
        // .dot
        // \d{4} four digits
        // space
        // \d{2} two digits
        // : colon
        // \d{2} two digits
        // : colon
        // \d{2} two digits
        // $ End of the line
        /^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2}):(\d{2})$/,

        // ^ Start of the line
        // \d{4} four digits
        // - dash
        // \d{2} two digits
        // - dash
        // \d{4} four digits
        // space
        // \d{2} two digits
        // : colon
        // \d{2} two digits
        // : colon
        // \d{2} two digits
        // $ End of the line
        /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/,
    ];
    for (let i = 0; i < formats.length; ++i) {
        parts = dateString.match(formats[i]);
        if (parts) {
            break;
        }
    }

    // If wrong format just return
    if (!parts) {
        return null;
    }

    // Remove leading zeros
    [1, 2, 4, 5, 6].forEach((i: number) => {
        parts[i] = Number((parts as RegExpMatchArray)[i].replace(/^0+/, ''));
    });
    /** Array is converted to numbers with forEach in previous code block */
    const formattedParts: Array<number> = parts as Array<number>;

    let returnDate: Date;
    if (formats[1].exec(dateString)) {
        returnDate = new Date(
            formattedParts[1],
            --formattedParts[2],
            formattedParts[3],
            formattedParts[4],
            formattedParts[5],
            formattedParts[6]
        );
    } else {
        returnDate = new Date(
            formattedParts[3],
            --formattedParts[2],
            formattedParts[1],
            formattedParts[4],
            formattedParts[5],
            formattedParts[6]
        );
    }
    return returnDate;
};

/**
 * Parses the string in format 'dd.mm.yyyy hh:mm:ss' and returns the Date.
 * @memberof Helpers
 * @param {Date} date
 * @returns {string}
 */
const makeStringFromDate = (date: Date): string => {
    const dateParts: Array<number | string> = [
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
    ];

    // Add leading zeros
    [1, 2, 3, 4, 5].forEach(
        (index: number) => (dateParts[index] = addLeadingZero(dateParts[index]))
    );

    return `${dateParts[2]}.${dateParts[1]}.${dateParts[0]} ${dateParts[3]}:${dateParts[4]}:${dateParts[5]}`;
};

/**
 * @callback PopoverHandler
 * @returns {PopoverHandlerResult}
 */

/**
 * @typedef PopoverHandlerResult
 * @property {string} x x coordinates
 * @property {string} y y coordinates
 * @property {string} align align of popover
 * @property {string} shift shift in pixels
 */

/**
 * @typedef IPopoverConfig
 * @property {Function | number} [popoverHeight] Popover height
 * @property {Function | number} [verticalOffset] Vertical offset
 * @property {Function | number} [popoverWidth] Popover width
 * @property {Function | number} [horizontalOffset] Horizontal offset
 * @property {Function | boolean} [invertHorizontalAlignment] Make popover aligned by right side
 * @property {Function | boolean} [supportMiddleAlignment] allow to use middle alignment
 * @property {Function | boolean} [supportTopAlignment] allow to use top alignment
 * @property {Function | boolean} [supportBottomAlignment=true] allow to use bottom alignment
 * @property {Function | boolean} [horizontalAxis] force to use horizontal axis
 * @property {Function | "left" | "right"} [horizontalAxisSide] default side for horizontal axis
 * @property {Function | "top" | "middle" | "bottom"} [verticalAxisSide]
 * Default side for horizontal axis
 */

/**
 * Creates handler for popover
 * @memberof Helpers
 * @param {HTMLElement} element
 * @param {IPopoverConfig} config
 * @returns {PopoverHandler}
 */
const makePopoverHandler = (
    element: HTMLElement,
    config: Partial<PopoverConfig>
): PopoverHandler => {
    const resolveValue = (value: PopoverConfig[keyof PopoverConfig]) => {
        return isFunction(value) ? value() : value;
    };
    const params = Object.assign<PopoverConfig, unknown>(
        {
            popoverHeight: 0,
            popoverWidth: 0,
            verticalOffset: 0,
            horizontalOffset: 0,
            topOffset: 0,
            invertHorizontalAlignment: false,
            supportMiddleAlignment: true,
            supportTopAlignment: true,
            supportBottomAlignment: true,
            horizontalAxis: false,
            horizontalAxisSide: null,
            verticalAxisSide: null,
        },
        config
    );
    return (): PopoverHandlerResult => {
        const popoverHeight = resolveValue(
            params.popoverHeight
        ) as PopoverConfig['popoverHeight'];
        const verticalOffset = resolveValue(
            params.verticalOffset
        ) as PopoverConfig['verticalOffset'];
        const popoverWidth = resolveValue(
            params.popoverWidth
        ) as PopoverConfig['popoverWidth'];
        const horizontalOffset = resolveValue(
            params.horizontalOffset
        ) as PopoverConfig['horizontalOffset'];
        const topOffset = resolveValue(
            params.topOffset
        ) as PopoverConfig['topOffset'];
        const invertHorizontalAlignment = resolveValue(
            params.invertHorizontalAlignment
        ) as PopoverConfig['invertHorizontalAlignment'];
        const supportMiddleAlignment = resolveValue(
            params.supportMiddleAlignment
        ) as PopoverConfig['supportMiddleAlignment'];
        const supportTopAlignment = resolveValue(
            params.supportTopAlignment
        ) as PopoverConfig['supportTopAlignment'];
        const supportBottomAlignment = resolveValue(
            params.supportBottomAlignment
        ) as PopoverConfig['supportBottomAlignment'];
        const horizontalAxis = resolveValue(
            params.horizontalAxis
        ) as PopoverConfig['horizontalAxis'];
        const verticalAxisSide = resolveValue(
            params.verticalAxisSide
        ) as PopoverConfig['verticalAxisSide'];
        const horizontalAxisSide = resolveValue(
            params.horizontalAxisSide
        ) as PopoverConfig['horizontalAxisSide'];

        const rect: DOMRect = element.getBoundingClientRect();
        const doc: HTMLElement = document.documentElement;

        let align: string;
        let shift: string = PopoverShifting.None;
        let xStart: number = 0;
        let xEnd: number;
        let yStart: number = 0;
        let yEnd: number;

        let verticalScrollOffset = doc.scrollTop
            ? doc.scrollTop
            : document.body.scrollTop;
        let horizontalScrollOffset = doc.scrollLeft
            ? doc.scrollLeft
            : document.body.scrollLeft;
        if (isInFixedLayer(element)) {
            verticalScrollOffset = 0;
            horizontalScrollOffset = 0;
        }

        const setVerticalAxisBottomPosition = () => {
            align = PopoverAlignment.Bottom;
            yStart =
                verticalScrollOffset + rect.bottom + (verticalOffset as number);
            yEnd =
                yStart + (verticalOffset as number) + (popoverHeight as number);
        };

        const setVerticalAxisTopPosition = () => {
            align = PopoverAlignment.Top;
            yStart =
                verticalScrollOffset +
                rect.top -
                (verticalOffset as number) -
                (popoverHeight as number) -
                (topOffset as number);
            yEnd =
                yStart + (verticalOffset as number) + (popoverHeight as number);
        };

        const setVerticalAxisMiddlePosition = () => {
            align = PopoverAlignment.Middle;
            if (
                (popoverHeight as number) + (verticalOffset as number) * 2 >
                window.innerHeight
            ) {
                yStart = (verticalOffset as number) + verticalScrollOffset;
            } else {
                yStart =
                    (window.innerHeight - (popoverHeight as number)) / 2 +
                    verticalScrollOffset;
            }
        };

        const setVerticalAxisHorizontalPosition = () => {
            if (invertHorizontalAlignment) {
                xEnd = rect.right - (horizontalOffset as number);
                xStart = xEnd - (popoverWidth as number);
                if (xStart < 0) {
                    xStart = 0;
                    shift = PopoverShifting.Right;
                }
            } else {
                xStart = rect.left + (horizontalOffset as number);
                xEnd = xStart + (popoverWidth as number);
                if (window.innerWidth < xEnd) {
                    xStart =
                        xStart -
                        (xEnd - rect.right) -
                        (horizontalOffset as number);
                    shift = PopoverShifting.Left;
                    if (xStart < 0) {
                        xStart = 0;
                    }
                }
            }
        };

        const setHorizontalAxisRightPosition = () => {
            align = PopoverAlignment.Right;
            yStart =
                verticalScrollOffset + rect.top + (verticalOffset as number);
            yEnd =
                yStart + (verticalOffset as number) + (popoverHeight as number);
            xStart = rect.right + (horizontalOffset as number);
            xEnd = xStart + (popoverWidth as number);
        };

        const setHorizontalAxisLeftPosition = () => {
            align = PopoverAlignment.Left;
            yStart =
                verticalScrollOffset + rect.top + (verticalOffset as number);
            yEnd =
                yStart + (verticalOffset as number) + (popoverHeight as number);
            xEnd = rect.left - (horizontalOffset as number);
            xStart = xEnd - (popoverWidth as number);
        };

        const isHorizontallyFit = () => {
            return (
                xStart >= horizontalScrollOffset &&
                xEnd <= window.innerWidth + horizontalScrollOffset
            );
        };

        const isVerticallyFit = () => {
            return (
                yStart >= verticalScrollOffset &&
                yEnd <= window.innerHeight + verticalScrollOffset
            );
        };

        if (!horizontalAxis) {
            align = (verticalAxisSide as string) || PopoverAlignment.Bottom;
            setVerticalAxisHorizontalPosition();
            if (align === PopoverAlignment.Bottom) {
                setVerticalAxisBottomPosition();
                let match = isVerticallyFit();
                if (!match && supportTopAlignment) {
                    setVerticalAxisTopPosition();
                    match = isVerticallyFit();
                }
                if (!match && supportBottomAlignment) {
                    setVerticalAxisBottomPosition();
                    match = isVerticallyFit();
                }
                if (!match && supportMiddleAlignment) {
                    setVerticalAxisMiddlePosition();
                }
            } else {
                setVerticalAxisTopPosition();
                let match = isVerticallyFit();
                if (!match && supportBottomAlignment) {
                    setVerticalAxisBottomPosition();
                    match = isVerticallyFit();
                }
                if (!match && supportMiddleAlignment) {
                    setVerticalAxisMiddlePosition();
                }
            }
        } else {
            align = (horizontalAxisSide as string) || PopoverAlignment.Right;
            if (align === PopoverAlignment.Right) {
                setHorizontalAxisRightPosition();
                if (!isHorizontallyFit()) {
                    setHorizontalAxisLeftPosition();
                }
            } else {
                setHorizontalAxisLeftPosition();
                if (!isHorizontallyFit()) {
                    setHorizontalAxisRightPosition();
                }
            }
        }

        element.setAttribute('data-popover-align', align);

        return {
            x: `${xStart}px`,
            y: `${yStart}px`,
            align,
            shift,
        };
    };
};

/**
 * Handles popover max height on resize.
 * @memberof Helpers
 * @param {HTMLElement | null} popover
 * @param {number} [vOffset]
 * @returns {Function}
 */
const makePopoverMaxHeightHandler = (
    popover: HTMLElement | null,
    vOffset: number = 0
): (() => void) => {
    const handler = () => {
        if (!popover) {
            return;
        }
        // menu-padding-bottom + menu-padding-top + offset x = 24 + 24 + 16 * 2 = 96
        const maxHeight =
            window.innerHeight -
            window.innerHeight / 4 -
            vOffset -
            getBaseGridPoint() * 8;
        popover.style.maxHeight = `${maxHeight}px`;
    };
    window.addEventListener('resize', handler);
    return handler;
};

/**
 * Checks if the given width fits within the window's inner width.
 * @param {number} width - The width to check.
 * @returns {boolean} - True if the width fits, false otherwise.
 */
const fitsHorizontally = (width: number) => {
    return width <= window.innerWidth;
};

/**
 * Processes inline scripts/
 * @param {HTMLScriptElement} originalScript
 * @private
 */
const processInlineScript = (originalScript: HTMLScriptElement) => {
    const newScript = document.createElement('script');
    Array.from(originalScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
    });
    newScript.appendChild(document.createTextNode(originalScript.innerHTML));
    originalScript.parentNode?.replaceChild(newScript, originalScript);
};

/**
 * Processes external scripts with 'src' attribute.
 * @param {HTMLScriptElement} originalScript
 * @returns {Promise<object>}
 * @private
 */
const processExternalScript = (
    originalScript: HTMLScriptElement
): Promise<object> => {
    return new Promise((resolve, reject) => {
        const newScript = document.createElement('script');
        newScript.onload = resolve;
        newScript.onerror = reject;
        Array.from(originalScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
        });
        originalScript.parentNode?.replaceChild(newScript, originalScript);
    });
};

/**
 * Rebuilds scripts from &lt;script> tags. Usually used in SPA context.
 * @memberof Helpers
 * @param {HTMLElement|DocumentFragment} content
 * @returns {Promise<void>}
 */
const rebuildScripts = async (
    content: HTMLElement | DocumentFragment
): Promise<Array<void>> => {
    const scripts = content.querySelectorAll<HTMLScriptElement>(
        'script[type="text/javascript"],script:not([type])'
    );
    const syncQueue: Array<() => void> = [];
    const asyncQueue: Array<() => Promise<object>> = [];
    scripts.forEach((script) => {
        if (script.hasAttribute('src')) {
            asyncQueue.push(() => processExternalScript(script));
        } else {
            syncQueue.push(() => processInlineScript(script));
        }
    });
    await Promise.all(asyncQueue.map((fn) => fn()));
    return syncQueue.map((fn_1) => fn_1());
};

/**
 * Scrolls window to element with offset. Uses ease function.
 * @memberof Helpers
 * @param {number|HTMLElement} target
 * @param {number} [offset]
 * @returns {Promise<void>}
 */
const scrollTo = (
    target: number | HTMLElement,
    offset: number = 0
): Promise<void> => {
    offset = offset || 0;
    const start = window.scrollY;
    const documentHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
    );
    const bodyTopPosition = document.body.getBoundingClientRect().top;
    const windowHeight =
        window.innerHeight ||
        document.documentElement.clientHeight ||
        document.getElementsByTagName('body')[0].clientHeight;

    let targetOffset;
    if (typeof target === 'number') {
        targetOffset = target;
    } else {
        targetOffset = target.getBoundingClientRect().top - bodyTopPosition;
    }

    const targetOffsetToScroll = Math.round(
        (documentHeight - targetOffset < windowHeight
            ? documentHeight - windowHeight
            : targetOffset) - offset
    );
    const duration = 300;
    const startTime = performance.now();
    const easing = (time: number): number => time * (2 - time);

    return new Promise((resolve) => {
        const scrollProgress = () => {
            const delta = performance.now() - startTime;
            const completed = delta >= duration;
            const progress = completed ? 1 : easing(delta / duration);
            const targetOffset =
                start + (targetOffsetToScroll - start) * progress;
            window.scroll(0, targetOffset);
            if (!completed) {
                requestAnimationFrame(scrollProgress);
            } else {
                resolve();
            }
        };
        scrollProgress();
    });
};

/**
 * Checks if the modal opener is inside fixed container.
 * @memberof Helpers
 * @param {HTMLElement | UIElement | EventTarget} element
 * @returns {boolean}
 */
const isInFixedLayer = (
    element: HTMLElement | UIElement | EventTarget
): boolean => {
    if (!element) {
        return false;
    }
    let parentOffset = (element as HTMLElement).offsetParent;
    while (parentOffset !== null) {
        if (window.getComputedStyle(parentOffset).position === 'fixed') {
            return true;
        }
        parentOffset = (parentOffset as HTMLElement).offsetParent;
    }
    return false;
};

/**
 * Checks if this is value is a plain object. Helper to avoid code duplication
 * and help to migrate from jQuery.
 * @memberof Helpers
 * @param {unknown} o
 * @returns {boolean}
 */
const isPlainObject = (o: unknown): o is object => {
    return (
        o != null &&
        !Array.isArray(o) &&
        Object.prototype.toString.call(o) === '[object Object]'
    );
};

/**
 * Checks if this is value is a plain object or array. Helper to avoid code duplication
 * and help to migrate from jQuery.
 * @memberof Helpers
 * @param {unknown} o
 * @returns {boolean}
 */
const isPlainObjectOrArray = (o: unknown): o is object | Array<unknown> => {
    return isPlainObject(o) || Array.isArray(o);
};

/**
 * Checks if this is value is a function.
 * @memberof Helpers
 * @param {unknown} value
 * @returns {boolean}
 */
const isFunction = (value: unknown): value is () => void => {
    return typeof value === 'function';
};

/**
 * Assemble query string from Object, for example, for ibank needs: to send in request body
 * @memberof Helpers
 * @param {object} params
 * @param {boolean} [traditional] - `true' for backwards compatibility.
 * @returns {string}
 */
const buildQueryString = (
    params: object,
    traditional: boolean = false
): string => {
    const paramsCopy: object = Object.assign({}, params);
    if (traditional) {
        Object.keys(paramsCopy).map((key) => {
            if (
                paramsCopy[key as keyof typeof paramsCopy] === null ||
                paramsCopy[key as keyof typeof paramsCopy] === undefined
            ) {
                (paramsCopy as Record<string, string>)[key] = '';
            }
        });
    }
    return new URLSearchParams(paramsCopy as Record<string, string>).toString();
};

/**
 * Function will be invoked in the next frame
 * @memberof Helpers
 * @param {Function} func Function that needs to be executed.
 */
const nextFrame = (func: () => void): void => {
    new Promise(window.requestAnimationFrame).then(() => func());
};

/**
 * Returns languages codes for given country.
 * @memberof Helpers
 * @param {("EE" | "LV" | "LT")} [country]
 * @returns {{short: Array<string>, long: Array<string>}}
 */
const getLanguageForCountry = (country: Country): LanguageLabels => {
    const config: Record<
        Country,
        { short: Array<LanguageShort>; long: Array<LanguageLong> }
    > = {
        [Country.Estonia]: {
            short: [LanguageShort.Estonian, LanguageShort.English],
            long: [LanguageLong.Estonian, LanguageLong.English],
        },
        [Country.Latvia]: {
            short: [LanguageShort.Latvian, LanguageShort.English],
            long: [LanguageLong.Latvian, LanguageLong.English],
        },
        [Country.Lithuania]: {
            short: [LanguageShort.Lithuanian, LanguageShort.English],
            long: [LanguageLong.Lithuanian, LanguageLong.English],
        },
    };

    const base = country ? config[country] : undefined;
    if (!base) {
        return {
            short: [
                LanguageShort.Estonian,
                LanguageShort.Latvian,
                LanguageShort.Lithuanian,
                LanguageShort.English,
                LanguageShort.Russian,
            ],
            long: [
                LanguageLong.Estonian,
                LanguageLong.Latvian,
                LanguageLong.Lithuanian,
                LanguageLong.English,
                LanguageLong.Russian,
            ],
        };
    }

    return {
        short: [
            ...base.short,
            ...(featureFlags.hasLanguageInCountry(country, LanguageLong.Russian)
                ? [LanguageShort.Russian]
                : []),
        ],
        long: [
            ...base.long,
            ...(featureFlags.hasLanguageInCountry(country, LanguageLong.Russian)
                ? [LanguageLong.Russian]
                : []),
        ],
    };
};

/**
 * Format given number to currency value
 * @memberof Helpers
 * @param {number} value
 * @param {string} [thousandSeparator] -  is used to separate groups of thousands
 * @param {number} [fractionDigits] -  amount of decimals numbers after comma
 * @param {boolean} [removeTrailingZeros] - remove zeroes,
 * when there are only zeroes after decimal point;
 * @returns {string}
 */
const formatCurrencyValue = (
    value: number,
    thousandSeparator: string = ' ',
    fractionDigits: number = 2,
    removeTrailingZeros: boolean = false
): UndefinedOr<string> => {
    let result = value
        .toFixed(fractionDigits)
        .replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

    if (removeTrailingZeros) {
        result = result.replace(/\.0+$/, '');
    }

    return fractionDigits === 0 ? result.split('.').shift() : result;
};

/**
 * Regular expression to increment indexes for ui-duplicate and
 * other duplicate items.
 *
 * Regexp explanatiion
 * 1. /\D*$
 * - \D: This matches any character that is not a digit (0-9).
 * - *: This means "zero or more" of the preceding token (non-digits in this case).
 * - $: This asserts the end of the string.
 * So, /\D*$/ matches any trailing non-digit characters at the end of a string. It essentially looks for any
 * sequence of non-digit characters until the end of the string.
 * or (|)
 * 2. (\[?)
 * - \[: This matches the literal character [.
 * - ?: This makes the preceding character (the opening bracket [) optional. It matches zero or one occurrence of [.
 * This part matches an optional opening bracket.
 * 3. (-?\d+)
 * - -?: This matches an optional minus sign (-), so it can match both positive and negative numbers.
 * - \d+: This matches one or more digits (0-9).
 * This part matches an optional negative number or a positive number.
 * 4. (\D*)
 * - \D*: This matches zero or more non-digit characters, just like in the first part (\D*$).
 * - This part matches any non-digit characters that come after the number.
 * 5. (\[])*
 * - \[]: This matches the literal characters [] (an empty pair of square brackets).
 * - *: This allows zero or more occurrences of [] to match.
 * This part matches any number of empty square brackets [].
 * $ End of the line
 * @memberof Helpers
 * @type {RegExp}
 */
const INDEX_REGEXP: RegExp = /\D*$|(\[?)(-?\d+)(\D*)(\[])*$/;

/**
 * Resolves indexes for duplication. User for 'name', 'id', 'for' attributes.
 * Rules for settings index:
 * name="hello" -> name="hello-1"
 * name="hello0" -> name="hello1"
 * name="hello[0]" -> name="hello[1]"
 * @memberof Helpers
 * @param {Element|UIElement} node
 * @param {string} attrName
 * @param {number|string} index
 */
const duplicateIndexSetter = (
    node: Element | UIElement,
    attrName: string,
    index: number | string
) => {
    const value = node.getAttribute(attrName) || '';
    node.setAttribute(
        attrName,
        value.replace(INDEX_REGEXP, (matches, $1, $2, $3) => {
            // 1. If not numbers found: nothing to increment, just add index to the end.
            // 2. If value equals match then no number suffix found, add index to end.
            // 3. If it has '-' in the beginning the just append it to value.
            // This is useful for attributes 'id' and 'label' also provides
            // backwards compatibility with older solution.
            if (
                !$2 ||
                matches === value ||
                String($2).startsWith('-') ||
                ($2 !== '' && Math.abs(+$2))
            ) {
                return [matches, index].join('-');
            }

            // Standard set for all other cases.
            return ($1 || '') + index + ($3 || '');
        })
    );
};

/**
 * Resolves video host.
 * @memberof Helpers
 * @param {string} src - Video source URL.
 * @returns {("youtube" | "unknown")}
 */
const resolveVideoHost = (src: string): VideoHostLabel => {
    const matches = /(youtube\.com|youtu\.be)/.exec(String(src));
    return matches ? VideoHost.YouTube : VideoHost.Unknown;
};

/**
 * Checks for modal-controller existence in DOM. If modal controller
 * doesn't exist then force add it the end of the body.
 * Make sure your component imports modal-controller component.
 * @memberof Helpers
 */
const appendModalControllerIfRequired = (): void => {
    let modalController = document.querySelector('ui-modal-controller');
    if (!modalController) {
        modalController = document.createElement('ui-modal-controller');
        document.body.appendChild(modalController);
    }
};

/**
 * Gets days count in given year and month.
 * @memberof Helpers
 * @param {number} year
 * @param {number} month
 * @returns {number}
 */
const getDaysInMonth = (year: number, month: number): number =>
    new Date(year, month + 1, 0).getDate();

/**
 * @memberof Helpers
 * @param {object} o
 * @returns {Readonly<object>}
 */
const deepFreeze = <T extends object>(o: T): Readonly<T> => {
    Object.keys(o).forEach((prop) => {
        if (
            isPlainObject(o[prop as keyof T]) &&
            !Object.isFrozen(o[prop as keyof T])
        ) {
            deepFreeze<T>(o[prop as keyof T] as T);
        }
    });
    return Object.freeze<T>(o);
};

/**
 * Converts array to object, index based on even/odd indexes key-pair.
 * E.g. ['a', 100, 'b', 200] => {a: 100, b: 200}
 * @memberof Helpers
 * @param {Array<unknown>} array
 * @returns {Record<string|number, unknown>}
 */
const arrayToObject = (array: Array<unknown>): ObjectWithStringOrNumberKeys => {
    const convertedArray: ObjectWithStringOrNumberKeys = {};
    let index: number = 0;
    while (index < array.length) {
        convertedArray[array[index++] as number] = array[index++];
    }
    return convertedArray;
};

/**
 * Minifies HTML template.
 * @memberof Helpers
 * @param {string} template
 * @returns {string}
 */
const minifyHTML = (template: string): string =>
    template
        .replace(/[\s]?\n[\s]*/g, ' ')
        .replace(/>[\s\t]*</g, '><')
        .trim();

/**
 * Clamp method.
 * @memberof Helpers
 * @param {number} minimumValue
 * @param {number} value
 * @param {number} maximumValue
 * @returns {number}
 */
const clamp = (
    minimumValue: number,
    value: number,
    maximumValue: number
): number => {
    return Math.max(Math.min(value, maximumValue), minimumValue);
};

/**
 * Checks if value exists in the object.
 * @memberof Helpers
 * @param {Record<string|unknown>} object
 * @param {unknown} value
 * @returns {boolean}
 */
const hasValue = (object: ObjectWithStringKeys, value: unknown): boolean => {
    return !!Object.values(object).find(
        (objectValue: unknown): boolean => objectValue === value
    );
};

/**
 * Wait when animation finished for given element. It takes into account
 * prefers-reduced-motion media query. If motion is reduced and no animation
 * then the promise is resolved immediately.
 * @memberof Helpers
 * @param {HTMLElement} elem
 * @returns {Promise<Animation[]>}
 */
const waitForAnimations = async (
    elem: HTMLElement
): Promise<Array<Animation>> => {
    return Promise.all(
        (elem?.getAnimations() || []).map((animation) => animation.finished)
    );
};

/**
 * Lower cases the first letter in the string.
 * @memberof Helpers
 * @param {string} string
 * @returns {string}
 */
const lowerCaseFirstLetter = (string: string): string => {
    return string[0].toLowerCase() + string.slice(1);
};

/**
 * Convert string booleans to booleans
 * @memberof Helpers
 * @param {boolean | string | number} recordKey
 * @returns {boolean | string | number}
 */
const getFormattedRecordKey = (
    recordKey: boolean | string | number
): boolean | string | number => {
    if (recordKey === 'true') {
        return true;
    } else if (recordKey === 'false') {
        return false;
    }
    return recordKey;
};

/**
 * Return typed keys for an any object.
 * @memberof Helpers
 * @example
 * enum Color {
 *     RED,
 *     GREEN,
 *     BLUE,
 * }
 * const colors = {
 *     [Color.RED]: 'red',
 *     [Color.GREEN]: 'green',
 *     [Color.BLUE]: 'blue',
 * };
 *
 * const keys1 = typedKeys(colors);
 * // typedKeys does same as:
 * const keys2 = Object.keys(colors).map((k) => isNaN(Number(k)) ? k : Number(k)) as Array<keyof typeof colors>;
 * @param {object} o
 * @returns {object}
 */
const typedKeys = <T extends object>(o: T): Array<keyof T> => {
    return Object.keys(o).map((k) =>
        isNaN(Number(k)) ? k : Number(k)
    ) as Array<keyof T>;
};

/**
 * Type guard for Element.
 * @memberof Helpers
 * @param {*} node
 * @returns {boolean}
 */
const isElementNode = (node: Node | ChildNode): node is HTMLElement =>
    node.nodeType === Node.ELEMENT_NODE;

/**
 * Type guard for Node instances.
 * @memberof Helpers
 * @param {*} value
 * @returns {boolean}
 */
const isNode = (value: unknown): value is Node => value instanceof Node;

/**
 * type guard for ElementConfig.
 * @memberof Helpers
 * @param {*} value
 * @returns {boolean}
 */
const isElementConfig = (value: unknown): value is ElementConfig =>
    isPlainObject(value);

/**
 * Type guard for DocumentFragment.
 * @memberof Helpers
 * @param {*} value
 * @returns {boolean}
 */
const isDocumentFragment = (value: unknown): value is DocumentFragment =>
    value instanceof DocumentFragment;

/**
 * Finds object key by value.
 * @param {object} object
 * @param {string} value
 * @returns {string | undefined}
 */
const getKeyByValue = <T extends Record<string, string>>(
    object: T,
    value: string
): string | undefined =>
    Object.keys(object).find((key) => object[key] === value);

export {
    debounce,
    throttle,
    makeDateFromString,
    makeStringFromDate,
    addLeadingZero,
    makePopoverHandler,
    isInFixedLayer,
    rebuildScripts,
    scrollTo,
    isPlainObject,
    buildQueryString,
    nextFrame,
    formatCurrencyValue,
    getLanguageForCountry,
    duplicateIndexSetter,
    resolveVideoHost,
    appendModalControllerIfRequired,
    getDaysInMonth,
    deepFreeze,
    arrayToObject,
    minifyHTML,
    clamp,
    hasValue,
    getKeyByValue,
    waitForAnimations,
    makePopoverMaxHeightHandler,
    fitsHorizontally,
    lowerCaseFirstLetter,
    getFormattedRecordKey,
    isPlainObjectOrArray,
    typedKeys,
    INDEX_REGEXP,
    isElementNode,
    isNode,
    isElementConfig,
    isDocumentFragment,
    featureFlags,
    makeInvertiblePopoverHandler,
    makeMenuPopover,
};
